<script setup>
import {useAlertStore} from '@toolify/client/src/stores/AlertStore/useAlertStore'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import InputComponent from '../../forms/components/inputs/InputComponent.vue'
import ButtonComponent from '../../ui/components/ButtonComponent.vue'
import {PropertyStrategyThemeType} from '../../tableItems/enum/PropertyStrategyThemeType'
import IconButtonComponent from '../../ui/components/IconButtonComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {StyleVariantType} from '@toolify/client/src/modules/ui/enum/StyleVariantType'
import {CypressUniqueNameType} from '@toolify/client/cypress/types/CypressUniqueNameType'

const notificationStore = useAlertStore()
const {
  message,
  color,
  description,
  inputValue,
  buttons,
  layout,
  isCentered,
  showInput,
  iconNameType,
  iconWeightType,
  iconColor,
  showCover,
  duration,
  position,
} = $(notificationStore.payload)
const {setInput} = $(notificationStore)

// Use iconNameType and iconWeightType directly from the store
const computedIconName = $computed(() => {
  return iconNameType || IconNameType.Bell
})

const computedWeightType = $computed(() => {
  return iconWeightType || IconWeightType.Solid
})

const lineAnimationDuration = $computed(() => {
  return (duration / 1000) + 's'
})

const computedBackgroundColor = $computed(() => {
  const styleMap = {
    success: '#08253d',
    error: '#2d0f14',
  }
  return styleMap[color]
})
const lineColor = $computed(() => {
  const styleMap = {
    success: '#558FEF',
    error: '#93000A',
  }
  return styleMap[color]
})
const hoverButtonColor = $computed(() => {
  const styleMap = {
    success: '#0086e6',
    error: '#93000A',
  }
  return styleMap[color]
})
const iconColorStyle = $computed(() => {
  const styleMap = {
    success: 'var(--primary-color)',
    error: 'var(--error-color)',
  }
  return iconColor === 'primary' ? styleMap[color] : 'var(--content-dim-color)'
})
const isHorizontal = $computed(() => {
  return layout === 'horizontal'
})

const layoutStyle = $computed(() => {
  return isHorizontal ? {
    flexDirection: 'column',
    alignItems: isCentered ? 'center' : 'flex-start',
    padding: '12px',
    gap: '12px',
  } : {
    flexDirection: 'row',
    padding: '12px 16px',
  }
})
const alertWidth = $computed(() => {
  return isHorizontal
    ? 'fit-content'
    : 'auto'
})
const computedPositionClass = $computed(() => {
  return position
})
const onButtonClick = (button) => {
  if (button.onClick) {
    button.onClick()
  }
}
</script>
<template>
  <div data-overlay-ignore>
    <div class="notification" :class="[computedPositionClass, {'is-horizontal': isHorizontal, 'is-centered': isCentered}]" :data-cy="CypressUniqueNameType.NotificationComponent">
      <div class="content" :style="layoutStyle">
        <IconComponent
          :name="computedIconName"
          :weightType="computedWeightType"
          class="icon"
          v-if="layout !== 'horizontal'"
        />
        <div class="message" :data-cy="CypressUniqueNameType.NotificationMessage">
          <span>{{message}}</span>

          <IconButtonComponent
            v-if="layout === 'horizontal'"
            :name="IconNameType.Xmark"
            :styleVariant="StyleVariantType.ClearClear"
            @click="notificationStore.close"
            :colorVariant="color==='success' ? 'primary' : 'error'"
            :data-cy="CypressUniqueNameType.NotificationClose"
            class="close-button"
          />
        </div>
        <div v-if="description" class="description" :data-cy="CypressUniqueNameType.NotificationDescription">{{description}} </div>
        <div class="input" v-if="showInput">
          <InputComponent
            fullWidth
            :theme="PropertyStrategyThemeType.Outlined"
            :value="inputValue"
            @update:modelValue="setInput"
            :data-cy="CypressUniqueNameType.NotificationInput"
          />
        </div>
        <div
          :key="inputValue"
          :class="{'button-row': buttons.length > 1}"
          v-if="buttons.length"
        >
          <ButtonComponent
            v-for="button in buttons"
            :key="button.text"
            :color="button.color"
            :style="button.style"
            :iconNameType="button.iconNameType"
            :isDisabled="button.disabled"
            @click="onButtonClick(button)"
            :data-cy="button.dataCy ?? CypressUniqueNameType.NotificationButton"
          >
            {{button.text}}
          </ButtonComponent>
        </div>
        <div
          v-if="layout !== 'horizontal'"
          @click="notificationStore.close"
          class="dismiss-button"
        >
          <IconComponent :name="IconNameType.Times" :weightType="IconWeightType.Solid"/>
        </div>
      </div>
      <div v-if="duration>0" class="line"/>
    </div>
    <div v-if="showCover" class="cover" @click="notificationStore.close"/>
  </div>
</template>
<style scoped lang="scss">
  @use '../../../styles/variables' as variables;

.notification {
  width: v-bind(alertWidth);
  background-color: v-bind(computedBackgroundColor);
  position: absolute;
  @include variables.zIndex(notification);
  border-radius: map-get(variables.$radiuses, radius-lg);
  box-shadow: map-get(variables.$shadows, shadow-toast);
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  overflow: hidden;
}
.notification.is-horizontal {
  min-width: 320px;
  min-inline-size: 320px;
  max-width: min(560px, calc(100vw - 32px));
}
.line {
  width: 100%;
  height: 4px;
  background-color: v-bind(lineColor);
  animation: shrink v-bind(lineAnimationDuration) linear;
}
@keyframes shrink {
  0% {
    width: 100%;
  }
  100% {
    width: 0;
  }
}
.content {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-2);
  height: 100%;
  padding: 0 map-get(variables.$spacing, space-4);
  color: white;
  
  font-weight: 500;
}
.message {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.notification.is-horizontal.is-centered .message {
  position: relative;
  justify-content: center;
}
.notification.is-horizontal.is-centered .message > span {
  text-align: center;
}
.notification.is-horizontal.is-centered :deep(.close-button) {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}
.message > span {
  flex: 1;
  min-width: 0;
}
.button-row {
  width: auto;
  display: grid;
  align-items: center;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: map-get(variables.$spacing, space-2);
}
.notification.is-horizontal .button-row {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: map-get(variables.$spacing, space-2);
}
.notification.is-horizontal .button-row :deep(.button) {
  white-space: normal;
  word-break: break-word;
  overflow: visible;
  text-overflow: clip;
  height: auto;
  padding-top: map-get(variables.$spacing, space-2);
  padding-bottom: map-get(variables.$spacing, space-2);
  line-height: 1.2;
  flex: 0 1 auto;
  max-width: 100%;
}
.notification.is-horizontal .message {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}
.dismiss-button {
  padding: map-get(variables.$spacing, space-1) map-get(variables.$spacing, space-2); 
  border-radius: map-get(variables.$radiuses, radius-sm);
  cursor: pointer;
  color: white;
  
  font-weight: 500;
  &:hover {
    background-color: v-bind(hoverButtonColor);
  }
}
.input{
  width: 100%;
}
.icon{
  color: v-bind(iconColorStyle);
}
.cover{
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: rgba(0, 0, 0, 0.5);
}
.top-center{
  top: 5vh;
  left: 50%;
  transform: translateX(-50%);
}
.bottom-right{
  bottom: 8px;
  right: 8px;
}
</style>
